import { useEffect, useState, useMemo } from "react";
import api, { API } from "../lib/apiClient";
import { resolveImageUrl } from "../lib/images";
import { Search, X, Phone, Mail, Tag, Loader2, FileDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Link } from "react-router-dom";

export default function Location() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState("Tous");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/equipment")
      .then((res) => { if (!cancelled) setItems(res.data); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return ["Tous", ...Array.from(set).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      const okCat = active === "Tous" || i.category === active;
      const q = query.trim().toLowerCase();
      const okQ = !q || i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q);
      return okCat && okQ;
    });
  }, [items, active, query]);

  return (
    <div data-testid="page-location">
      <section className="relative bg-[#0F172A] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-35"
          style={{ backgroundImage: "url(https://customer-assets.emergentagent.com/job_e8a51ae8-0e5c-48cb-98d6-63c31c891959/artifacts/1hchlr4q_Marketing%20-%20Location%20de%20mat%C3%A9riels.jpg)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/85 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#FBBF24] mb-4">Catalogue 2026</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight max-w-3xl">
            Location de matériel professionnel
          </h1>
          <p className="mt-5 text-lg text-slate-300 max-w-2xl">
            Nacelles, groupes électrogènes, échafaudages, compresseurs et bien plus. Livraison rapide sur toute l'île de La Réunion.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="catalog-search"
              placeholder="Rechercher un matériel…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 h-11 rounded-full border-slate-200"
            />
          </div>
          <a href={`${API}/equipment/export.pdf`} target="_blank" rel="noreferrer" className="w-full lg:w-auto">
            <Button variant="outline" className="h-11 rounded-full border-slate-200 w-full" data-testid="catalog-pdf">
              <FileDown className="w-4 h-4 mr-2" /> Télécharger le catalogue PDF
            </Button>
          </a>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                active === c
                  ? "bg-[#1B3A6B] text-white border-[#1B3A6B]"
                  : "bg-white text-slate-700 border-slate-200 hover:border-[#1B3A6B] hover:text-[#1B3A6B]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading && (
          <div className="py-20 flex items-center justify-center text-slate-500" data-testid="catalog-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement du catalogue…
          </div>
        )}

        {error && <div className="py-10 text-center text-red-600">{error}</div>}

        {!loading && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((it) => (
              <button
                key={it.id}
                onClick={() => setSelected(it)}
                data-testid={`catalog-item-${it.id}`}
                className="text-left group rounded-2xl bg-white border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(27,58,107,0.25)] transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden bg-slate-100">
                  {it.image_url ? (
                    <img src={resolveImageUrl(it.image_url)} alt={it.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">No image</div>
                  )}
                  <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm border border-slate-200 text-[11px] font-semibold text-[#1B3A6B] flex items-center gap-1">
                    <Tag className="w-3 h-3" /> {it.category}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-slate-900 tracking-tight line-clamp-2 group-hover:text-[#1B3A6B] transition-colors">{it.name}</h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-2 leading-relaxed">{it.description}</p>
                  {it.price_info && it.show_price !== false && (
                    <div className="mt-4 text-sm font-semibold text-[#FBBF24] line-clamp-1">{it.price_info.split("·")[0]}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="py-20 text-center text-slate-500" data-testid="catalog-empty">
            Aucun matériel ne correspond à votre recherche.
          </div>
        )}
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="catalog-dialog">
          {selected && (
            <>
              <div className="relative h-64 sm:h-80 bg-slate-100 overflow-hidden rounded-t-lg">
                {selected.image_url && <img src={resolveImageUrl(selected.image_url)} alt={selected.name} className="w-full h-full object-cover" />}
                <button
                  onClick={() => setSelected(null)}
                  data-testid="catalog-dialog-close"
                  className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center hover:bg-white"
                  aria-label="Fermer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-7 lg:p-8">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1B3A6B]/5 text-[#1B3A6B] text-xs font-semibold mb-3">
                  <Tag className="w-3 h-3" /> {selected.category}
                </div>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl sm:text-3xl tracking-tight">{selected.name}</DialogTitle>
                  <DialogDescription className="text-slate-600 mt-3 leading-relaxed">
                    {selected.description}
                  </DialogDescription>
                </DialogHeader>

                {selected.specs?.length > 0 && (
                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">Caractéristiques</div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      {selected.specs.map((s, i) => (
                        <li key={i} className="text-sm text-slate-700 flex gap-2"><span className="text-[#FBBF24] mt-1">•</span>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {selected.price_info && selected.show_price !== false && (
                  <div className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-200">
                    <div className="text-xs uppercase tracking-wider font-semibold text-amber-900 mb-1">Tarification</div>
                    <div className="text-sm text-amber-900 leading-relaxed">{selected.price_info}</div>
                  </div>
                )}

                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to={`/contact?subject=Devis%20${encodeURIComponent(selected.name)}&activity=Location`} onClick={() => setSelected(null)}>
                    <Button className="bg-[#1B3A6B] hover:bg-[#142c54] rounded-full px-6 h-11" data-testid="catalog-dialog-quote">
                      Demander un devis
                    </Button>
                  </Link>
                  <a href="tel:0692371331">
                    <Button variant="outline" className="rounded-full px-6 h-11 border-slate-200" data-testid="catalog-dialog-call">
                      <Phone className="w-4 h-4 mr-2" /> 0692 37 13 31
                    </Button>
                  </a>
                  <a href="mailto:contact@ingelec.re">
                    <Button variant="outline" className="rounded-full px-6 h-11 border-slate-200" data-testid="catalog-dialog-mail">
                      <Mail className="w-4 h-4 mr-2" /> Email
                    </Button>
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
